/* eslint-disable */
import React from 'react';
import moment from 'moment';
import productionIcon from '@/Images/produced.svg';
import { CirclePlus, CookingPot, XCircle } from 'lucide-react';

/**
 * Default Plan Status Icon Component for Production Plans
 */
const DefaultPlanStatusIcon = ({ headerId, generatedPlans }) => {
    const dateStr = headerId.replace('date_', '');
    const plan = generatedPlans
        ?.filter(p => p.scheduledAt === dateStr || moment(p.scheduledAt).format('YYYY-MM-DD') === dateStr)
        ?.sort((a, b) => (b.id || 0) - (a.id || 0))?.[0];

    const isPast = moment(dateStr).isBefore(moment(), 'day');
    const isCancelled = plan?.status?.toUpperCase() === 'CANCELLED';

    if (isCancelled) {
        if (isPast) return '';
        return <CirclePlus size={20} className='bg-yellow-400 rounded-full' />;
    } else if (isPast && !plan) {
        return <XCircle size={20} className='text-red-400 rounded-full p-0' />;
    } else if (plan) {
        return <CookingPot size={20} className='bg-green-600 rounded-full text-white p-1' />;
    }
    return <CirclePlus size={20} className='bg-yellow-400 rounded-full' />;
};


const GenerateRow = (props) => {
    const {
        fixedHeaders,
        scrollableHeaders,
        columnWidth,
        onGeneratePlan,
        onCellClick,
        scrollLeft,
        scrollableWidth,
        generatedPlans,
        isUnsaved,
        label = 'Generate Daily Production Plan',
        unsavedLabel = 'Please first save the plan then generate production plan',
        icon = productionIcon,
        renderCellStatus,
        getCellTitle,
        rowClass = 'bg-yellow-300 border-yellow-400',
        cellHoverClass = 'hover:bg-yellow-400',
    } = props;

    // Calculate total fixed width
    const fixedWidth = fixedHeaders.reduce((acc, header) => {
        return acc + (header.width || 120);
    }, 0);

    // Calculate total scrollable content width
    const totalScrollableWidth = scrollableHeaders.reduce((acc, header) => {
        return acc + (columnWidth || header.width || 80);
    }, 0);

    const defaultRenderStatus = (header) => (
        <DefaultPlanStatusIcon headerId={header.id} generatedPlans={generatedPlans} />
    );

    const defaultGetTitle = (header) => {
        if (isUnsaved) return unsavedLabel;
        const dateStr = header.id?.replace('date_', '');
        const plan = generatedPlans
            ?.filter(p => p.scheduledAt === dateStr || moment(p.scheduledAt).format('YYYY-MM-DD') === dateStr)
            ?.sort((a, b) => (b.id || 0) - (a.id || 0))?.[0];
        const isPast = moment(dateStr).isBefore(moment(), 'day');
        if (plan?.status?.toUpperCase() === 'CANCELLED') {
            if (isPast) return 'This plan is cancelled';
        }
        if (isPast && !plan) return 'No production plan was generated for this date';
        return `Generate plan for ${dateStr}`;
    };

    return (
        <div className={`flex w-full border-t ${rowClass}`}>
            {/* Fixed section with label */}
            <div
                className={`flex items-center gap-2 px-3 py-2 shrink-0 z-10 ${rowClass.split(' ')[0]}`}
                style={{
                    width: `${fixedWidth}px`,
                    boxShadow: scrollLeft > 0 ? '4px 0 6px -2px rgba(0, 0, 0, 0.1)' : 'none'
                }}
            >
                {icon && (typeof icon === 'string' ? (
                    <img src={icon} alt="" className='w-5 h-5' />
                ) : (
                    icon
                ))}
                <span className='font-medium text-gray-800 text-sm'>
                    {isUnsaved ? unsavedLabel : label}
                </span>
            </div>
            {/* Scrollable section container - clips the content */}
            <div
                className='flex-1 overflow-hidden'
                style={{ width: scrollableWidth ? `${scrollableWidth}px` : 'auto' }}
            >
                {/* Scrollable content - transforms based on table scroll */}
                <div
                    className='flex'
                    style={{
                        width: `${totalScrollableWidth}px`,
                        transform: `translateX(-${scrollLeft}px)`,
                        transition: 'none'
                    }}
                >
                    {scrollableHeaders.map((header) => {
                        const dateStr = header.id.replace('date_', '');
                        const existingPlan = generatedPlans
                            ?.filter(p => p.scheduledAt === dateStr || moment(p.scheduledAt).format('YYYY-MM-DD') === dateStr)
                            ?.sort((a, b) => (b.id || 0) - (a.id || 0))?.[0];
                        const isPast = moment(dateStr).isBefore(moment(), 'day');
                        const isCancelled = existingPlan?.status?.toUpperCase() === 'CANCELLED';
                        const isDisabled = isUnsaved || (isCancelled && isPast) || (isPast && !existingPlan);

                        return (
                            <div
                                key={header.id}
                                className={`flex items-center justify-center shrink-0 transition-colors py-2 
                                    ${isDisabled ? 'cursor-not-allowed' : `cursor-pointer ${cellHoverClass}`}`}
                                style={{
                                    width: columnWidth || header.width || 180,
                                    minWidth: columnWidth || header.width || 180,
                                    borderRight: header.isWeekEnd ? '2px solid #000' : 'none'
                                }}
                                onClick={() => {
                                    if (isDisabled) return;
                                    if (onCellClick) {
                                        onCellClick(header);
                                        return;
                                    }

                                    // Fallback to default Production Plan logic
                                    const isPast = moment(dateStr).isBefore(moment(), 'day');
                                    if (isPast && (!existingPlan || isCancelled)) return;
                                    onGeneratePlan?.(header, isCancelled ? undefined : existingPlan);
                                }}
                                title={getCellTitle ? getCellTitle(header) : defaultGetTitle(header)}
                            >
                                {renderCellStatus ? renderCellStatus(header) : defaultRenderStatus(header)}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default GenerateRow;
